<template>
    <div :class="{ 'min-h-screen': showCategoryMenu }">
        <div class="flex" :class="{ 'min-h-screen': showCategoryMenu }">
            <div v-if="showCategoryMenu" class="document-category-menu">
                <div class="text-white mt-4 mx-4 pb-2 mb-6 border-b border-dashed border-gray-400 uppercase">Categories</div>
                <ul class="categories">
                    <li class="w-full px-4 py-2 mb-2 all-documents" :class="[selectedCategory == '' ? 'active' : '']" @click="selectCategory('')">
                        <div class="flex justify-between items-center">
                            <div class="font-bold uppercase">All Documents</div>
                            <div class="document-count">{{ documents.all.length }}</div>
                        </div>
                    </li>
                    <li v-for="category in availableCategories" :key="category.id" :class="[selectedCategory == category.id ? 'active' : '']" @click="selectCategory(category.id)">
                        <div class="category" @click="toggleCategory(category.id)">
                            <div class="flex justify-between items-center">
                                <div class="font-bold uppercase">{{ category.name }}</div>
                                <div class="document-count">{{ category.document_count }}</div>
                            </div>
                        </div>
                        <transition name="fade">
                            <ul v-if="categoryVisible(category.id)" class="subcategories">
                                <li class="subcategory"
                                    v-for="subcategory in subcategoriesFor(category.id)" :key="subcategory.id"
                                    @click.stop="selectSubcategory(subcategory.id)"
                                    :class="{ active: selectedSubCategory == subcategory.id }">
                                    <div class="flex justify-between items-center">
                                        <div class="font-bold uppercase">{{ subcategory.name }}</div>
                                        <div class="ml-4 document-count">{{ subcategory.document_count }}</div>
                                    </div>
                                </li>
                            </ul>
                        </transition>
                    </li>
                </ul>
            </div>

            <div class="flex flex-1 flex-col" :class="[ showCategoryMenu ? 'h-full max-h-full overflow-y-auto flex-1 p-8' : 'p-0' ]">
                <div class="flex justify-between mb-5 border-b border-gray-200 pb-2">
                    <div class="flex items-center">
                        <h1 :class="[ showCategoryMenu ? 'page-header' : 'module-header']">Documents</h1>
                        <div class="ml-3 flex items-center text-gray-600">
                            <div class="text-2xl">/</div>
                            <div class="ml-2 pt-1 text-xl">{{ documents.filtered.length }} of {{ documents.all.length }}</div>
                        </div>
                    </div>
                    <div class="hidden lg:inline-flex items-center">
                        <filters
                            :show-view-options="false"
                            data-type="documents"
                            sort-by="category_name"
                            :filters="filters"
                            :show-client-filter="false"
                            :selected-view-option="layout"
                            @view-change="layout = $event">
                                <template slot="filters">
                                    <div class="flex items-center mb-4">
                                        <span class="mr-6">Category</span>
                                        <select v-model="selectedCategory" class="form-select">
                                            <option value=''>All</option>
                                            <option v-for="category in availableCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
                                        </select>
                                    </div>
                                    <div class="flex items-center">
                                        <span class="mr-6">Subcategory</span>
                                        <select v-model="selectedSubCategory" class="form-select">
                                            <option value=''>All</option>
                                            <option v-for="subcategory in availableSubcategories" :key="subcategory.id" :value="subcategory.id">{{ subcategory.name }}</option>
                                        </select>
                                    </div>
                                </template>
                        </filters>
                        <div v-if="$gate.can('create', 'document')" class="ml-6">
                            <button @click="showDocumentUpload" type="button" class="flex items-center btn btn-small btn-indigo">
                                <inline-svg name="upload" classes="w-4 h-4 text-white fill-current"></inline-svg>
                                <span class="ml-2">Upload</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="layout == 'grid' && documents.filtered.length">
                    <paginate name="documents" :list="documents.filtered" :per="perPage" class="paginate-list">
                        <div class="grid grid-cols-4 gap-6">
                            <div class="card" v-for="file in paginated('documents')" :key="file.id">
                                <div class="box box-indigo h-full">
                                    <div class="h-full flex flex-col">
                                        <div class="w-full h-48 flex items-center justify-center bg-gray-100 rounded-t-lg overflow-hidden">
                                            <div v-if="file.is_image">
                                                <a :href="`#${file.name}`">
                                                    <img :src="file.view_url" class="mx-auto w-full h-full object-cover rounded-md shadow-md">
                                                </a>
                                                <a href="#_" class="lightbox" :id="`${file.name}`">
                                                    <div class="relative">
                                                        <inline-svg name="close" classes="absolute top-0 right-0 fill-current text-white hover:text-gray-300 h-8 w-8 cursor-pointer"></inline-svg>
                                                        <img :src="file.view_url">
                                                    </div>
                                                </a>
                                            </div>
                                            <div v-else-if="file.is_video">
                                                <a :href="`#${file.name}`">
                                                    <img src="/img/video.png" class="mx-auto w-full h-full object-cover rounded-md shadow-md">
                                                </a>
                                                <a href="#_" class="lightbox" :id="`${file.name}`">
                                                    <video width="100%" height="100%" controls>
                                                      <source :src="file.view_url" :type="'video/' + file.type">
                                                    </video>
                                                </a>
                                            </div>
                                            <div v-else v-html="file.icon" class="w-10 h-10 mx-auto"></div>
                                        </div>
                                        <div class="p-5 flex flex-1 flex-col justify-between">
                                            <div class="flex justify-between">
                                                <div v-html="file.icon" class="w-10 h-10"></div>
                                                <div class="flex flex-col items-end leading-tight">
                                                    <span class="text-lg font-variant-all-small-caps">{{ file.category_name }}</span>
                                                    <span class="font-variant-all-small-caps">{{ file.subcategory_name }}</span>
                                                </div>
                                            </div>
                                            <div class="mt-2">
                                                <p class="text-center font-semibold mb-2" :title="file.name">{{ file.name | shortenFileName(15) }}</p>
                                                <p class="text-gray-800 leading-tight" style="min-height: 1.2rem;">{{ file.description }}</p>
                                            </div>
                                        </div>
                                        <div class="flex justify-between items-center bg-gray-100 p-3 text-sm">
                                            <div class="flex flex-col">
                                                <span class="font-semibold">{{ file.uploaded_by_name }}</span>
                                                <span class="text-sm">{{ file.uploaded_at }}</span>
                                            </div>
                                            <div class="flex items-center justify-between space-x-3">
                                                <button
                                                    type="button"
                                                    v-if="$gate.can('update', 'document', file)"
                                                    @click="showDocumentEdit(file.id)"
                                                    class="cursor-pointer"
                                                    title="Edit Document">
                                                        <inline-svg name="edit"></inline-svg>
                                                </button>
                                                <a :href="file.download_url" title="Download Document">
                                                    <inline-svg name="download"></inline-svg>
                                                </a>
                                                <button
                                                    type="button"
                                                    v-if="$gate.can('delete', 'document', file)"
                                                    @click="confirmDeleteDocument(file)"
                                                    class="cursor-pointer"
                                                    title="Delete Document">
                                                        <inline-svg name="trash"></inline-svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </paginate>
                    <paginate-links for="documents" :show-step-links="true" :hide-single-page="true"></paginate-links>
                </div>

                <div v-if="layout == 'table' && documents.filtered.length">
                    <table class="w-full">
                        <thead class="cursor-pointer">
                            <tr>
                                <th class="px-8"></th>
                                <th>Document</th>
                                <th>Category</th>
                                <th v-if="! fromReatlas" class="hidden lg:table-cell">Uploaded By</th>
                                <th v-if="! fromReatlas" class="text-center">Type</th>
                                <th class="text-right pr-6 lg:pr-0">Size</th>
                                <th class="hidden lg:table-cell"></th>
                            </tr>
                        </thead>
                        <paginate name="documents" :list="documents.filtered" :per="perPage" tag="tbody">
                            <tr v-for="file in paginated('documents')" :key="file.id">
                                <td>
                                    <div v-if="file.is_image">
                                        <a :href="`#${file.name}`">
                                            <img :src="file.view_url" class="mx-auto w-12 h-12 object-cover rounded-md shadow-md">
                                        </a>
                                        <a href="#_" class="lightbox" :id="`${file.name}`">
                                            <div class="relative">
                                                <inline-svg name="close" classes="absolute top-0 right-0 fill-current text-white hover:text-gray-300 h-8 w-8 cursor-pointer"></inline-svg>
                                                <img :src="file.view_url">
                                            </div>
                                        </a>
                                    </div>
                                    <div v-else-if="file.is_video">
                                        <a :href="`#${file.name}`">
                                            <div v-html="file.icon" class="w-10 h-10 mx-auto"></div>
                                        </a>
                                        <a href="#_" class="lightbox" :id="`${file.name}`">
                                            <video width="100%" height="100%" controls>
                                              <source :src="file.view_url" :type="'video/' + file.type">
                                            </video>
                                        </a>
                                    </div>
                                    <div v-else v-html="file.icon" class="w-10 h-10 mx-auto"></div>
                                </td>
                                <td>
                                    <div :title="file.name">
                                        <div v-if="file.is_image" class="font-semibold">
                                            <a :href="`#${file.name}`">
                                                {{ file.name | shortenFileName(20) }}
                                            </a>
                                            <a href="#_" class="lightbox" :id="`${file.name}`">
                                                <img :src="file.view_url">
                                            </a>
                                        </div>
                                        <div v-else-if="file.is_video" class="font-semibold">
                                            <a :href="`#${file.name}`">
                                                {{ file.name | shortenFileName(20) }}
                                            </a>
                                            <a href="#_" class="lightbox" :id="`${file.name}`">
                                                <video width="100%" height="100%" controls>
                                                  <source :src="file.view_url" :type="'video/' + file.type">
                                                Your browser does not support the video tag.
                                                </video>
                                            </a>
                                        </div>
                                        <a v-else :href="file.view_url" target="_blank" class="font-semibold">
                                            {{ file.name | shortenFileName(20) }}
                                        </a>
                                    </div>
                                    <p class="mt-1 max-w-xs">{{ file.description }}</p>
                                </td>
                                <td class="leading-tight">
                                    <p class="text-xl font-variant-all-small-caps">{{ file.category_name }}</p>
                                    <p class="font-variant-all-small-caps">{{ file.subcategory_name }}</p>
                                </td>
                                <td v-if="! fromReatlas" class="hidden lg:table-cell">
                                    <p>{{ file.uploaded_by_name }}</p>
                                    <p class="text-sm">{{ file.uploaded_at }}</p>
                                </td>
                                <td v-if="! fromReatlas" class="text-center">{{ file.type }}</td>
                                <td class="text-right pr-6 lg:pr-0">{{ file.readable_size }}</td>
                                <td class="pl-6 hidden lg:table-cell">
                                    <div class="flex items-center justify-center space-x-3">
                                        <button v-if="$gate.can('update', 'document', file)" @click="showDocumentEdit(file.id)" type="button">
                                            <inline-svg name="edit-pencil" classes="w-5 h-5 cursor-pointer"></inline-svg>
                                        </button>
                                        <button @click="download(file.download_url)" type="button">
                                            <inline-svg name="download" classes="text-orange-500 hover:text-orange-400 w-5 h-5"></inline-svg>
                                        </button>
                                        <button @click="confirmDeleteDocument(file)" type="button">
                                            <inline-svg name="trash"></inline-svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </paginate>
                    </table>
                    <paginate-links for="documents" :show-step-links="true" :hide-single-page="true"></paginate-links>
                </div>

                <div v-if="! documents.filtered.length">
                    <p class="none-found">No Documents Found</p>
                </div>
            </div>
        </div>

        <modal name="document-upload" width="60%" height="auto" :click-to-close="false" draggable=".modal-header">
            <upload-document
                :documentable-id="documentableId"
                :documentable-type="documentableType"
                @documents-uploaded="appendDocuments">
            </upload-document>
        </modal>

        <modal name="document-edit" width="50%" height="auto" draggable=".modal-header">
            <edit-document
                :fileId="selectedDocument"
                @document-updated="documentUpdated">
            </edit-document>
        </modal>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        name: 'ReoptDocuments',

        props: {
            documentData: {
                type: [Array, Object],
                required: false,
                // Neither documents/index.blade.php nor buildings/show.blade.php
                // passes this, and created() copies it straight into
                // documents.all and documents.filtered. Without a default that
                // wrote undefined over both, and every template reading
                // .length on them threw.
                default: () => []
            },

            documentableId: {
                type: Number,
                required: false
            },

            documentableType: {
                type: String,
                required: false
            },

            showCategoryMenu: {
                type: Boolean,
                default: false
            },

            sortBy: {
                type: String,
                required: false
            },

            sortAsc: {
                type: Boolean,
                default: true,
            },

            view: {
                type: String,
                default: 'table'
            },

            perPage: {
                type: Number,
                default: 12
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                paginate: ['documents'],
                layout: this.view,
                selectedCategory: '',
                selectedSubCategory: '',
                selectedDocument: '',
            }
        },

        created() {
            this.$store.dispatch('loadCategories');
        },

        mounted() {
            if (localStorage.getItem('reopt_documents_layout')) {
                this.layout = localStorage.getItem('reopt_documents_layout');
            }

            this.documents.all = this.documentData;
            this.documents.filtered = this.documentData;
        },

        watch: {
            documentData() {
                this.documents.all = this.documentData;
                this.documents.filtered = this.documentData;
            },

            filtered() {
                this.calculateDocumentCounts();
            },

            all() {
                this.calculateDocumentCounts();
            },

            availableCategories() {
                this.calculateDocumentCounts();
            },

            selectedCategory(categoryID) {
                this.selectCategory(categoryID);
                this.availableCategories[categoryID].visible = true;

                this.$emit('filterRequest');
            },

            selectedSubCategory() {
                this.$emit('filterRequest');
            }
        },

        computed: {
            ...mapState(['documents']),

            all() {
                return this.documents.all;
            },

            filtered() {
                return this.documents.filtered;
            },

            filters() {
                return {
                    category_id: this.selectedCategory,
                    subcategory_id: this.selectedSubCategory,
                }
            },

            availableCategories() {
                return _.each(_.keyBy(this.documents.categories, 'id'), (category) => {
                    category.visible = false;
                });
            },

            availableSubcategories() {
                if (this.selectedCategory == '') {
                    return '';
                }

                return _.filter(this.documents.subcategories, (item) => {
                    return item.category_id == this.selectedCategory
                })
            }
        },

        methods: {
            subcategoriesFor(categoryId) {
                // Was a v-if beside the v-for. The condition needs the outer loop's
                // category, so it is a method rather than a computed.
                return (this.documents.subcategories || []).filter(sub => sub.category_id == categoryId);
            },

            toggleCategory(categoryID) {
                this.availableCategories[categoryID].visible = ! this.availableCategories[categoryID].visible;
            },

            categoryVisible(categoryID) {
                return this.availableCategories[categoryID].visible;
            },

            selectCategory(categoryID) {
                if (this.selectedCategory != categoryID) {
                    this.selectedSubCategory = '';
                }

                this.selectedCategory = categoryID;
            },

            selectSubcategory(subcategoryID) {
                this.selectedCategory = _.find(this.documents.subcategories, ['id', subcategoryID]).category_id;
                this.selectedSubCategory = subcategoryID;
            },

            calculateDocumentCounts() {
                // categories
                _.each(this.availableCategories, (category) => {
                    category.document_count = _.filter(this.documents.all, (item) => {
                        return item.category_id == category.id
                    }).length
                });

                // subcategories
                _.each(this.documents.subcategories, (subcategory) => {
                    subcategory.document_count = _.filter(this.documents.all, (item) => {
                        return item.subcategory_id == subcategory.id
                    }).length;
                });
            },

            showDocumentUpload() {
                this.$modal.show('document-upload');
            },

            documentUpdated(documentData) {
                this.removeDocument(documentData.id);
                this.appendDocuments(typeof documentData == 'object' ? [documentData] : documentData);
            },

            appendDocuments(documents) {
                this.documents.all.push(...documents);

                this.$emit('filterRequest');
            },

            removeDocument(fileId) {
                this.documents.all = _.remove(this.documents.all, (file) => { return file.id != fileId });

                this.$emit('filterRequest');
            },

            showDocumentEdit(fileId) {
                this.selectedDocument = fileId;

                this.$modal.show('document-edit');
            },

            confirmDeleteDocument(file) {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this document?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteDocument(file);
                    }
                });
            },

            deleteDocument(file) {
                const self = this;

                axios.delete('/documents/' + file.id)
                    .then(function (response) {
                        self.removeDocument(file.id);

                        self.$eventHub.$emit('remove-from-gallery', file.view_url);
                    })
                    .catch(function (error) {
                        console.log(error);
                        self.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            },

            download(url) {
                window.location = url;
            },

            loadPreview(file) {
                file.show_preview = true;

                this.$forceUpdate();
            }
        }
    }
</script>
